// Status rendering. States and reasons come from the API (database view);
// this only names and colors them, always with text (never color alone).
import { AlertTriangle, CircleCheck, CirclePause, CircleSlash, Clock } from "lucide-react";
import type { EffectiveState, ServingReason, VersionState } from "@/api/types";
import { SERVING_STATES, VERSION_STATES } from "@/lib/labels";
import { Badge } from "@/ui/badge";

export function VersionStateBadge({ state }: { state: VersionState }) {
  const s = VERSION_STATES[state];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

const SERVING_ICONS = {
  SERVING: CircleCheck,
  PAUSED: CirclePause,
  SUSPENDED: AlertTriangle,
  NOT_READY: Clock,
  ARCHIVED: CircleSlash,
} as const;

export function ServingStateBadge({ state }: { state: EffectiveState }) {
  const s = SERVING_STATES[state];
  const Icon = SERVING_ICONS[state];
  return (
    <Badge tone={s.tone}>
      <Icon aria-hidden="true" />
      {s.label}
    </Badge>
  );
}

const AUTHORITY_LABEL = { TENANT: "tu equipo", PLATFORM: "InteractAds", BILLING: "facturación" } as const;

/** Why a Distribution is not serving: every reason the server reports, with the causing holds. */
export function ServingReasons({ reasons }: { reasons: readonly ServingReason[] }) {
  if (reasons.length === 0) {
    return <p className="text-sm text-muted">Sin bloqueos: la distribución puede servir.</p>;
  }
  return (
    <ul className="grid gap-2" aria-label="Motivos">
      {reasons.map((reason) => (
        <li key={reason.code} className="grid gap-1 rounded-md border border-line bg-sunken px-3 py-2 text-sm">
          <span>{reason.message}</span>
          {reason.holds.map((hold) => (
            <span key={hold.id} className="text-xs text-muted">
              Aplicada por {AUTHORITY_LABEL[hold.authority]} · {hold.reason}
              {hold.authority === "TENANT" ? "" : " · Solo quien la aplicó puede levantarla."}
            </span>
          ))}
        </li>
      ))}
    </ul>
  );
}
